import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  getDistricts, createDistrict, updateDistrict, deleteDistrict,
  exportDistricts, importDistricts, scanDistrictDuplicates,
  mergeDistricts, listDistrictAliases, createDistrictAlias, deleteDistrictAlias,
} from "../../lib/api";
import DistrictPortalAccessDialog from "./DistrictPortalAccessDialog";

const EMPTY = { name: "", contact_name: "", contact_email: "", contact_phone: "", contact_title: "", address_1: "", address_2: "", city: "", state: "", zip: "" };

export function SendingSchoolsTab() {
  const [districts, setDistricts] = useState([]);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [dupes, setDupes] = useState(null);
  const [importing, setImporting] = useState(false);
  const [portalDistrict, setPortalDistrict] = useState(null);
  const [aliases, setAliases] = useState([]);
  const [aliasForm, setAliasForm] = useState({ alias: "", canonical: "" });
  const [mergeGroup, setMergeGroup] = useState(null);
  const [mergeKeepId, setMergeKeepId] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const res = await getDistricts();
      setDistricts(res.data || []);
    } catch {
      toast.error("Failed to load sending schools");
    }
  };
  const loadAliases = async () => {
    try {
      const res = await listDistrictAliases();
      setAliases(res.data || []);
    } catch {
      /* aliases are admin-only; ignore for non-admins */
    }
  };
  useEffect(() => { load(); loadAliases(); }, []);

  const openAdd = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (d) => { setEditing(d); setForm({ name: d.name || "", contact_name: d.contact_name || "", contact_email: d.contact_email || "", contact_phone: d.contact_phone || "", contact_title: d.contact_title || "", address_1: d.address_1 || "", address_2: d.address_2 || "", city: d.city || "", state: d.state || "", zip: d.zip || "" }); setDialogOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    try {
      if (editing) {
        await updateDistrict(editing.id, form);
        toast.success("Sending school updated");
      } else {
        await createDistrict(form);
        toast.success("Sending school added");
        setDialogOpen(false);
        load();
        // Offer to set up portal access for the newly added district right away.
        setPortalDistrict(form.name.trim());
        return;
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.name}"? This won't change any student records already linked to it.`)) return;
    try {
      await deleteDistrict(d.id);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  const handleExport = async () => {
    try {
      const res = await exportDistricts();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "sending_schools_export.xlsx"; a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await importDistricts(fd);
      toast.success(res.data.message);
      if (res.data.duplicates_merged?.length) {
        toast.info(`Merged ${res.data.duplicates_merged.length} duplicate name(s) instead of creating new rows.`);
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const checkDuplicates = async () => {
    try {
      const res = await scanDistrictDuplicates();
      setDupes(res.data);
      const nExact = (res.data.exact_groups || res.data.duplicate_groups || []).length;
      const nSimilar = (res.data.similar_groups || []).length;
      if (nExact + nSimilar === 0) toast.success("No duplicate sending schools found");
    } catch {
      toast.error("Duplicate scan failed");
    }
  };

  const openMerge = (records, suggestedKeepId) => {
    setMergeGroup(records);
    setMergeKeepId(suggestedKeepId || records[0]?.id);
  };

  const confirmMerge = async () => {
    if (!mergeGroup || !mergeKeepId) return;
    const removeIds = mergeGroup.map((r) => r.id).filter((id) => id !== mergeKeepId);
    try {
      const res = await mergeDistricts({ keep_id: mergeKeepId, remove_ids: removeIds });
      toast.success(`Merged into "${res.data.kept}" — removed ${res.data.removed}, added ${res.data.aliases_added} alias(es)`);
      setMergeGroup(null);
      setMergeKeepId(null);
      await load();
      await loadAliases();
      await checkDuplicates();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Merge failed");
    }
  };

  const addAlias = async () => {
    const alias = aliasForm.alias.trim();
    const canonical = aliasForm.canonical.trim();
    if (!alias || !canonical) { toast.error("Enter a name variant and pick the district it belongs to"); return; }
    try {
      await createDistrictAlias({ alias, canonical });
      toast.success("Grouping alias saved");
      setAliasForm({ alias: "", canonical: "" });
      loadAliases();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save alias");
    }
  };

  const removeAlias = async (id) => {
    try {
      await deleteDistrictAlias(id);
      loadAliases();
    } catch {
      toast.error("Failed to delete alias");
    }
  };

  const filtered = districts.filter((d) => (d.name || "").toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  return (
    <div className="space-y-4" data-testid="sending-schools-tab">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-900">
        <strong>Sending Schools</strong> are the districts/schools that place students with you. They&apos;re
        kept separate from regular Contacts because they tie to contracts and student files. Upload uses a
        different template and de-duplicates by school name to avoid billing/file errors.
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Input placeholder="Search sending schools…" value={search} onChange={(e) => setSearch(e.target.value)} className="w-64" data-testid="sending-schools-search" />
        <div className="flex-1" />
        <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleImport} />
        <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={importing} data-testid="import-sending-schools-btn">
          {importing ? "Importing…" : "Upload / Update (Excel)"}
        </Button>
        <Button variant="outline" onClick={handleExport} data-testid="export-sending-schools-btn">Download (Excel)</Button>
        <Button variant="outline" onClick={checkDuplicates} data-testid="scan-sending-schools-dupes-btn">Find Duplicates</Button>
        <Button onClick={openAdd} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="add-sending-school-btn">+ Add Sending School</Button>
      </div>

      {dupes && ((dupes.exact_groups || dupes.duplicate_groups || []).length > 0 || (dupes.similar_groups || []).length > 0) && (
        <Card className="border-amber-300" data-testid="district-dupes-card">
          <CardContent className="py-3 space-y-3">
            {(dupes.exact_groups || dupes.duplicate_groups || []).length > 0 && (
              <div className="space-y-2">
                <div className="text-sm font-semibold text-amber-800">
                  {(dupes.exact_groups || dupes.duplicate_groups).length} exact duplicate group(s)
                </div>
                {(dupes.exact_groups || dupes.duplicate_groups).map((g, i) => (
                  <div key={`ex-${i}`} className="text-sm flex items-center gap-2">
                    <Badge variant="outline">{g.records.length}×</Badge>
                    <span className="flex-1">{g.name}</span>
                    <Button size="sm" variant="outline" className="h-7 px-2 text-xs"
                      onClick={() => openMerge(g.records, g.records[0]?.id)}
                      data-testid={`merge-exact-${i}`}>Merge…</Button>
                  </div>
                ))}
              </div>
            )}

            {(dupes.similar_groups || []).length > 0 && (
              <div className="space-y-2 border-t pt-2">
                <div className="text-sm font-semibold text-amber-800">
                  {dupes.similar_groups.length} possible variant group(s)
                </div>
                <p className="text-xs text-muted-foreground">
                  These look like the same district under different names (e.g. an abbreviation). Review, then merge.
                </p>
                {dupes.similar_groups.map((g, i) => (
                  <div key={`sim-${i}`} className="text-sm flex items-center gap-2">
                    <Badge variant="outline">{g.records.length}×</Badge>
                    <span className="flex-1">{g.records.map((r) => r.name).join("  ·  ")}</span>
                    <Button size="sm" variant="outline" className="h-7 px-2 text-xs"
                      onClick={() => openMerge(g.records, g.suggested_keep_id)}
                      data-testid={`merge-similar-${i}`}>Merge…</Button>
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Merging keeps one record and turns the removed name(s) into grouping aliases, so the Referral Trends
              dashboard still counts them together. Student records are never changed.
            </p>
          </CardContent>
        </Card>
      )}

      {/* District name grouping aliases (dashboard) */}
      <Card data-testid="district-aliases-card">
        <CardContent className="py-3 space-y-3">
          <div className="text-sm font-semibold text-[#00305E]">District name grouping (dashboard aliases)</div>
          <p className="text-xs text-muted-foreground">
            Map a name variant onto a real district so the Referral Trends dashboard groups them together — for cases
            the automatic matcher can&apos;t resolve (e.g. &quot;Mount Abe SD&quot; → &quot;Mount Abraham…&quot;). Display-only; students are untouched.
          </p>
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex-1 min-w-[180px]">
              <label className="text-xs font-medium">Name variant</label>
              <Input value={aliasForm.alias} placeholder="e.g. Mount Abe SD"
                onChange={(e) => setAliasForm((p) => ({ ...p, alias: e.target.value }))}
                data-testid="alias-variant-input" />
            </div>
            <div className="flex-1 min-w-[220px]">
              <label className="text-xs font-medium">Group under district</label>
              <Select value={aliasForm.canonical} onValueChange={(v) => setAliasForm((p) => ({ ...p, canonical: v }))}>
                <SelectTrigger data-testid="alias-canonical-select"><SelectValue placeholder="Select a district…" /></SelectTrigger>
                <SelectContent>
                  {districts.slice().sort((a, b) => (a.name || "").localeCompare(b.name || "")).map((d) => (
                    <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={addAlias} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="add-alias-btn">Add alias</Button>
          </div>
          {aliases.length > 0 && (
            <div className="space-y-1 pt-1" data-testid="alias-list">
              {aliases.map((a) => (
                <div key={a.id} className="text-sm flex items-center gap-2">
                  <span className="text-muted-foreground">{a.alias}</span>
                  <span className="text-slate-400">→</span>
                  <span className="flex-1 font-medium">{a.canonical}</span>
                  <Button variant="ghost" size="sm" className="h-6 px-2 text-xs text-red-600 hover:bg-red-50"
                    onClick={() => removeAlias(a.id)} data-testid={`delete-alias-${a.id}`}>Remove</Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm" data-testid="sending-schools-table">
            <thead>
              <tr className="border-b bg-gray-50 text-left">
                <th className="p-3">Sending School / District</th>
                <th className="p-3">Contact</th>
                <th className="p-3">Email</th>
                <th className="p-3">Phone</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">No sending schools yet. Add one or upload an Excel file.</td></tr>
              ) : filtered.map((d) => (
                <tr key={d.id} className="border-b hover:bg-gray-50" data-testid={`sending-school-row-${d.id}`}>
                  <td className="p-3 font-medium">{d.name}</td>
                  <td className="p-3">{d.contact_name || "—"}</td>
                  <td className="p-3">{d.contact_email || "—"}</td>
                  <td className="p-3">{d.contact_phone || "—"}</td>
                  <td className="p-3 text-right">
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-[#00305E] hover:bg-[#00305E]/10" onClick={() => setPortalDistrict(d.name)} data-testid={`portal-access-${d.id}`}>Portal Access</Button>
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => openEdit(d)} data-testid={`edit-sending-school-${d.id}`}>Edit</Button>
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-red-600 hover:bg-red-50" onClick={() => remove(d)} data-testid={`delete-sending-school-${d.id}`}>Del</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit Sending School" : "Add Sending School"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium">School / District Name *</label>
              <Input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} data-testid="sending-school-name-input" />
            </div>
            <div>
              <label className="text-sm font-medium">Contact Name</label>
              <Input value={form.contact_name} onChange={(e) => setForm((p) => ({ ...p, contact_name: e.target.value }))} data-testid="sending-school-contact-input" />
            </div>
            <div>
              <label className="text-sm font-medium">Contact Email</label>
              <Input value={form.contact_email} onChange={(e) => setForm((p) => ({ ...p, contact_email: e.target.value }))} data-testid="sending-school-email-input" />
            </div>
            <div>
              <label className="text-sm font-medium">Contact Phone</label>
              <Input value={form.contact_phone} onChange={(e) => setForm((p) => ({ ...p, contact_phone: e.target.value }))} data-testid="sending-school-phone-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">LEA Representative Title (for HHB letters)</Label>
              <Input value={form.contact_title} placeholder="e.g. Director of Special Education" onChange={(e) => setForm((p) => ({ ...p, contact_title: e.target.value }))} data-testid="sending-school-title-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Mailing Address Line 1</Label>
              <Input value={form.address_1} onChange={(e) => setForm((p) => ({ ...p, address_1: e.target.value }))} data-testid="sending-school-address1-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Mailing Address Line 2</Label>
              <Input value={form.address_2} onChange={(e) => setForm((p) => ({ ...p, address_2: e.target.value }))} data-testid="sending-school-address2-input" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">City</Label>
                <Input value={form.city} onChange={(e) => setForm((p) => ({ ...p, city: e.target.value }))} data-testid="sending-school-city-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">State</Label>
                <Input value={form.state} onChange={(e) => setForm((p) => ({ ...p, state: e.target.value }))} data-testid="sending-school-state-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">ZIP</Label>
                <Input value={form.zip} onChange={(e) => setForm((p) => ({ ...p, zip: e.target.value }))} data-testid="sending-school-zip-input" />
              </div>
            </div>
            <p className="text-xs text-amber-600">LEA title + mailing address are used on HHB notification letters. Fill them in if blank.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="save-sending-school-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DistrictPortalAccessDialog
        district={portalDistrict}
        open={!!portalDistrict}
        onOpenChange={(v) => { if (!v) setPortalDistrict(null); }}
      />

      <Dialog open={!!mergeGroup} onOpenChange={(v) => { if (!v) { setMergeGroup(null); setMergeKeepId(null); } }}>
        <DialogContent data-testid="merge-districts-dialog">
          <DialogHeader><DialogTitle>Merge duplicate districts</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Choose the name to KEEP. The others will be deleted and turned into grouping aliases so the
              Referral Trends dashboard still counts them together. Student records are not changed.
            </p>
            <div className="space-y-2">
              {(mergeGroup || []).map((r) => (
                <label key={r.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`merge-keep-option-${r.id}`}>
                  <input type="radio" name="merge-keep" checked={mergeKeepId === r.id}
                    onChange={() => setMergeKeepId(r.id)} />
                  <span className={mergeKeepId === r.id ? "font-semibold" : ""}>{r.name}</span>
                  {mergeKeepId === r.id
                    ? <Badge className="bg-emerald-600">Keep</Badge>
                    : <Badge variant="outline" className="text-red-600">Remove → alias</Badge>}
                </label>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setMergeGroup(null); setMergeKeepId(null); }}>Cancel</Button>
            <Button onClick={confirmMerge} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="confirm-merge-btn">Merge</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
